import { XCircleIcon } from 'lucide-react';
import { ReactNode, useMemo } from 'react';
import type { ServerSearch } from '@/shared/hooks';
import { IconButton } from '@/shared/ui';
import { AddNestedItemsDialog } from './AddNestedItemsDialog';
import { NestedItemCard } from './NestedItemCard';

type Labels = {
  title: string;
  addButton: string;
  /** Название действия у кнопки-значка исключения элемента: с указанием, что и откуда убирают */
  removeButton: string;
  emptyState: string;
  dialogTitle: string;
  searchPlaceholder?: string;
  noItemsFound?: string;
  /** Кнопка подтверждения окна выбора; по умолчанию — «Применить» */
  apply?: string;
};

type NestedItemsProps<T> = {
  labels: Labels;
  value: T[];
  onChange: (items: T[]) => void;
  allItems: T[];
  /**
   * Сколько записей в справочнике всего, если `allItems` — лишь первое окно его выдачи: окно выбора
   * сообщит, сколько показано из скольких. Без него справочник считается прочитанным целиком
   */
  total?: number;
  /** Записи `allItems` загружаются: окно выбора показывает загрузку, а не пустую выдачу */
  isLoading?: boolean;
  renderTitle: (item: T) => ReactNode;
  /** Отличительный признак записи (например, код): в строке состава под названием, в окне выбора справа */
  renderHint?: (item: T) => ReactNode;
  renderDescription?: (item: T) => ReactNode;
  getItemId: (item: T) => string;
  disabled?: boolean;
  /**
   * Пополнение состава доступно. Без него состав только сокращается: например, справочник для
   * выбора закрыт правами, а исключению элемента справочник не нужен
   */
  canAdd?: boolean;
  className?: string;
  searchFilter?: (item: T, searchQuery: string) => boolean;
  /**
   * Строка поиска окна выбора, если `allItems` — выдача сервера под неё (`useServerSearch`): справочник
   * ищется по всему набору, а `searchFilter` не нужен. Без неё окно отбирает `allItems` на месте
   */
  serverSearch?: ServerSearch;
};

export const NestedItems = <T,>({
  labels,
  value,
  onChange,
  allItems,
  total,
  isLoading,
  renderTitle,
  renderHint,
  renderDescription,
  getItemId,
  disabled = false,
  canAdd = true,
  className,
  searchFilter,
  serverSearch,
}: NestedItemsProps<T>) => {
  const handleRemoveItem = (id: string) => {
    onChange(value.filter((item) => getItemId(item) !== id));
  };

  // Строка берёт запись справочника, если она там есть: запись состава может нести не все поля
  // (описание). Записи за окном выдачи справочника строка рисуется из самой записи состава, а не
  // пропадает из карточки
  const rows = useMemo(() => {
    const itemsById = new Map(allItems.map((item) => [getItemId(item), item]));
    return value.map((item) => itemsById.get(getItemId(item)) ?? item);
  }, [value, allItems, getItemId]);

  return (
    <div className={`flex flex-col gap-4 ${className || ''}`}>
      <div className="flex gap-5 items-center">
        <p className="font-medium">{labels.title}</p>

        {!disabled && canAdd && (
          <AddNestedItemsDialog
            allItems={allItems}
            total={total}
            isLoading={isLoading}
            value={value}
            onSave={onChange}
            labels={labels}
            renderTitle={renderTitle}
            renderHint={renderHint}
            renderDescription={renderDescription}
            getItemId={getItemId}
            searchFilter={searchFilter}
            serverSearch={serverSearch}
          />
        )}
      </div>

      <div className="flex flex-col gap-3">
        {rows.map((item) => (
          <NestedItemCard
            key={getItemId(item)}
            item={item}
            renderTitle={renderTitle}
            renderHint={renderHint}
            renderDescription={renderDescription}
            action={
              !disabled && (
                <IconButton
                  icon={<XCircleIcon />}
                  color="destructive"
                  label={labels.removeButton}
                  onClick={() => handleRemoveItem(getItemId(item))}
                />
              )
            }
          />
        ))}
        {value.length === 0 && <p className="text-sm text-muted-foreground">{labels.emptyState}</p>}
      </div>
    </div>
  );
};
